import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { t } from '@/i18n'
import {
  getExtractionJobs,
  getExtractionUsage,
  uploadScreenshots,
  type ExtractionJobOut,
} from '@/lib/api'
import { formatDateTime } from '@/lib/format'

const STATUS_KEY: Record<ExtractionJobOut['status'], string> = {
  queued: 'st.queued',
  running: 'st.running',
  review: 'st.review',
  committed: 'st.committed',
  rejected: 'st.rejected',
  failed: 'st.failed',
}

export function Screenshots() {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [isPrivate, setIsPrivate] = useState(false)

  const jobs = useQuery({
    queryKey: ['extraction', 'jobs'],
    queryFn: () => getExtractionJobs(),
    refetchInterval: 4_000,
  })
  const usage = useQuery({ queryKey: ['extraction', 'usage'], queryFn: getExtractionUsage })

  const upload = useMutation({
    mutationFn: ({ files, privateFlag }: { files: File[]; privateFlag: boolean }) =>
      uploadScreenshots(files, privateFlag),
    onSuccess: (data) => {
      const dupes = data.filter((item) => item.duplicate).length
      setMessage(
        dupes
          ? t('shots.acceptedDup', { n: data.length, d: dupes })
          : t('shots.accepted', { n: data.length }),
      )
      void queryClient.invalidateQueries({ queryKey: ['extraction'] })
    },
    onError: (error) => {
      setMessage(error instanceof Error ? error.message : t('shots.uploadFail'))
    },
  })

  const takeFiles = useCallback(
    (list: FileList | File[] | null) => {
      if (!list) return
      const files = Array.from(list).filter(
        (file) => file.type.startsWith('image/') || file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'),
      )
      if (files.length === 0) {
        setMessage(t('shots.needImg'))
        return
      }
      upload.mutate({ files, privateFlag: isPrivate })
    },
    [upload, isPrivate],
  )

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const files = event.clipboardData?.files
      if (files && files.length > 0) takeFiles(files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [takeFiles])

  const reviewFirst = (jobs.data ?? []).find((job) => job.status === 'review')

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('shots.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('shots.lead')}</p>
        </div>
        {reviewFirst && (
          <Link
            to={`/screenshots/${reviewFirst.id}`}
            className="rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-900"
          >
            {t('shots.queue')}
          </Link>
        )}
      </div>

      {usage.data?.warning && (
        <p className="mt-4 rounded-md border border-pending/30 bg-pending/10 px-3 py-2 text-sm text-pending">
          {usage.data.warning}
        </p>
      )}
      {usage.data && (
        <p className="mt-2 text-xs text-slate-600">
          {t('shots.vision', { spent: usage.data.spent_usd, budget: usage.data.budget_usd })}
        </p>
      )}

      <div
        className={[
          'mt-6 cursor-pointer rounded-lg border border-dashed px-6 py-10 text-center text-sm',
          dragOver ? 'border-pending bg-pending/5 text-slate-200' : 'border-surface-border text-slate-500',
        ].join(' ')}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragOver(false)
          takeFiles(event.dataTransfer.files)
        }}
      >
        {t('shots.drop')}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={(event) => {
            takeFiles(event.target.files)
            event.target.value = ''
          }}
        />
      </div>

      <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-slate-400">
        <input
          type="checkbox"
          className="rounded border-surface-border"
          checked={isPrivate}
          onChange={(event) => setIsPrivate(event.target.checked)}
        />
        {t('shots.private')}
      </label>

      {upload.isPending && <p className="mt-3 text-sm text-slate-500">{t('shots.uploading')}</p>}
      {message && <p className="mt-3 text-sm text-slate-300">{message}</p>}

      <ul className="mt-8 divide-y divide-surface-border rounded-lg border border-surface-border bg-surface-raised">
        {(jobs.data ?? []).length === 0 && (
          <li className="px-4 py-8 text-center text-sm text-slate-500">{t('shots.empty')}</li>
        )}
        {(jobs.data ?? []).map((job) => (
          <li key={job.id}>
            <Link
              to={job.status === 'review' || job.status === 'failed' ? `/screenshots/${job.id}` : `/screenshots/${job.id}`}
              className="flex items-center justify-between gap-4 px-4 py-3 text-sm hover:bg-slate-700/20"
            >
              <div>
                <div className="text-slate-200">{job.original_filename || job.id.slice(0, 8)}</div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {formatDateTime(job.created_at)}
                  {job.detected_exchange ? ` · ${job.detected_exchange}` : ''}
                  {job.private ? t('shots.privateTag') : ''}
                  {job.provider ? ` · ${job.provider}` : ''}
                  {job.error ? ` · ${job.error}` : ''}
                </div>
              </div>
              <span
                className={[
                  'num shrink-0 rounded px-2 py-0.5 text-[11px]',
                  job.status === 'review'
                    ? 'bg-pending/15 text-pending'
                    : job.status === 'committed'
                      ? 'bg-profit/15 text-profit'
                      : job.status === 'failed' || job.status === 'rejected'
                        ? 'bg-loss/15 text-loss'
                        : 'bg-slate-700/40 text-slate-400',
                ].join(' ')}
              >
                {t(STATUS_KEY[job.status])}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
